/**
 * AugmentationSelectionModal Component
 * Modal for selecting augmentations at game start
 * Uses inline styles for consistent appearance
 */

import type { Augmentation } from "@cloudnest/augmented-chess-engine";
import {
  getAugmentation,
  getColorDisplayName,
  getPieceTypeDisplayName,
} from "@cloudnest/augmented-chess-engine";
import { useEffect, useMemo, useState } from "react";
import type { AugmentationSelectionModalProps } from "../../types";

/** Styles for the modal overlay */
const overlayStyles: React.CSSProperties = {
  position: "fixed",
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: "rgba(0, 0, 0, 0.4)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  zIndex: 1000,
};

/** Styles for the modal container */
const modalStyles: React.CSSProperties = {
  backgroundColor: "#fff",
  borderRadius: "12px",
  padding: "24px",
  maxWidth: "600px",
  width: "90%",
  color: "#333",
  boxShadow: "0 4px 20px rgba(0,0,0,0.15)",
};

/** Styles for the header */
const headerStyles: React.CSSProperties = {
  textAlign: "center",
  marginBottom: "20px",
};

/** Styles for the choices container */
const choicesContainerStyles: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: "12px",
};

/** Styles for an augmentation card */
const cardStyles: React.CSSProperties = {
  backgroundColor: "#f8f8f8",
  border: "2px solid #ddd",
  borderRadius: "8px",
  padding: "16px",
  cursor: "pointer",
  textAlign: "left",
  transition: "all 0.2s ease",
};

/** Styles for a selected card */
const selectedCardStyles: React.CSSProperties = {
  ...cardStyles,
  backgroundColor: "#e8f5e9",
  borderColor: "#4caf50",
};

/** Styles for the card title */
const cardTitleStyles: React.CSSProperties = {
  margin: "0 0 8px 0",
  fontSize: "1.1rem",
  fontWeight: "bold",
  color: "#333",
};

/** Styles for the card description */
const cardDescriptionStyles: React.CSSProperties = {
  margin: "0 0 8px 0",
  fontSize: "0.9rem",
  color: "#555",
};

/** Styles for the affected pieces */
const cardPiecesStyles: React.CSSProperties = {
  fontSize: "0.8rem",
  color: "#888",
};

/** Styles for the status indicator */
const statusStyles: React.CSSProperties = {
  textAlign: "center",
  marginTop: "16px",
  padding: "8px",
  backgroundColor: "#f5f5f5",
  borderRadius: "4px",
  fontSize: "0.9rem",
  border: "1px solid #e0e0e0",
};

const noteStyles: React.CSSProperties = {
  marginTop: "10px",
  padding: "10px 12px",
  borderRadius: "6px",
  border: "1px solid #dbeafe",
  backgroundColor: "#eff6ff",
  color: "#1e40af",
  fontSize: "0.85rem",
};

const progressGridStyles: React.CSSProperties = {
  marginTop: "12px",
  marginBottom: "12px",
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
  gap: "8px",
};

const progressCardStyles: React.CSSProperties = {
  border: "1px solid #e5e7eb",
  borderRadius: "8px",
  backgroundColor: "#f9fafb",
  padding: "10px",
};

const progressHeaderStyles: React.CSSProperties = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  gap: "8px",
  marginBottom: "6px",
};

const progressTitleStyles: React.CSSProperties = {
  fontSize: "0.85rem",
  fontWeight: 700,
  color: "#1f2937",
};

const progressCountStyles: React.CSSProperties = {
  fontSize: "0.8rem",
  fontWeight: 600,
  color: "#166534",
};

const progressListStyles: React.CSSProperties = {
  margin: 0,
  paddingInlineStart: "18px",
  color: "#4b5563",
  fontSize: "0.8rem",
};

const progressEmptyStyles: React.CSSProperties = {
  margin: 0,
  color: "#6b7280",
  fontSize: "0.8rem",
};

const progressPreviewStyles: React.CSSProperties = {
  margin: "8px 0 0 0",
  color: "#1d4ed8",
  fontSize: "0.78rem",
  fontWeight: 600,
};

const actionsStyles: React.CSSProperties = {
  marginTop: "14px",
  display: "flex",
  flexDirection: "column",
  gap: "8px",
};

const selectedPreviewStyles: React.CSSProperties = {
  margin: 0,
  textAlign: "center",
  color: "#1f2937",
  fontSize: "0.85rem",
};

const approveButtonStyles: React.CSSProperties = {
  border: "none",
  borderRadius: "8px",
  backgroundColor: "#16a34a",
  color: "#fff",
  padding: "10px 12px",
  fontWeight: 700,
  fontSize: "0.9rem",
  cursor: "pointer",
};

/** Props for AugmentationCard */
interface AugmentationCardProps {
  augmentation: Augmentation;
  onSelect: () => void;
  isSelected: boolean;
  disabled: boolean;
}

/** Single augmentation card component */
function AugmentationCard({
  augmentation,
  onSelect,
  isSelected,
  disabled,
}: AugmentationCardProps): JSX.Element {
  const baseStyle = isSelected ? selectedCardStyles : cardStyles;
  const style: React.CSSProperties = {
    ...baseStyle,
    opacity: disabled ? 0.6 : 1,
    cursor: disabled ? "not-allowed" : "pointer",
  };

  return (
    <button
      type="button"
      onClick={disabled ? undefined : onSelect}
      style={style}
      disabled={disabled}
    >
      <h3 style={cardTitleStyles}>
        {augmentation.nameKo ?? augmentation.name}
      </h3>
      <p style={cardDescriptionStyles}>
        {augmentation.descriptionKo ?? augmentation.description}
      </p>
      <span style={cardPiecesStyles}>
        적용 대상:{" "}
        {augmentation.targetPieceTypes.map(getPieceTypeDisplayName).join(", ")}
      </span>
    </button>
  );
}

/** Main augmentation selection modal component */
export function AugmentationSelectionModal({
  gameState,
  augmentationChoices,
  selectingPlayer,
  onSelectAugmentation,
}: AugmentationSelectionModalProps): JSX.Element | null {
  // All hooks must be called before any early return (Rules of Hooks)

  // Get pending selections (safe regardless of phase)
  const whitePending = gameState.augmentations.white.pendingSelection;
  const blackPending = gameState.augmentations.black.pendingSelection;

  const whiteActiveAugmentations = useMemo(
    () =>
      gameState.augmentations.white.active
        .map((id) => getAugmentation(id))
        .filter((aug): aug is Augmentation => aug !== undefined),
    [gameState.augmentations.white.active],
  );

  const blackActiveAugmentations = useMemo(
    () =>
      gameState.augmentations.black.active
        .map((id) => getAugmentation(id))
        .filter((aug): aug is Augmentation => aug !== undefined),
    [gameState.augmentations.black.active],
  );

  const currentPendingSelection =
    selectingPlayer === "WHITE"
      ? whitePending
      : selectingPlayer === "BLACK"
        ? blackPending
        : null;

  const [stagedSelectionId, setStagedSelectionId] = useState<string | null>(
    currentPendingSelection ?? null,
  );

  useEffect(() => {
    setStagedSelectionId(currentPendingSelection ?? null);
  }, [currentPendingSelection]);

  // Don't show if not in augmentation selection phase
  if (
    gameState.phase !== "AUGMENTATION_SELECTION" ||
    !gameState.augmentationSelection
  ) {
    return null;
  }

  const { whiteSelected, blackSelected } = gameState.augmentationSelection;

  const stagedSelection = stagedSelectionId
    ? (getAugmentation(stagedSelectionId) ?? null)
    : null;

  const canConfirmSelection = Boolean(selectingPlayer && stagedSelectionId);

  const handleConfirmSelection = () => {
    if (!selectingPlayer || !stagedSelectionId) return;
    onSelectAugmentation(stagedSelectionId, selectingPlayer);
  };

  return (
    <div style={overlayStyles}>
      <div style={modalStyles}>
        <div style={headerStyles}>
          <h2 style={{ margin: "0 0 8px 0" }}>증강 선택</h2>
          <p style={{ margin: 0, color: "#666" }}>
            선택한 증강은 매 라운드마다 누적되어 적용됩니다
          </p>
        </div>

        {/* Selection status */}
        <div style={statusStyles}>
          <span style={{ color: whiteSelected ? "#4caf50" : "#ff9800" }}>
            백:{" "}
            {whiteSelected
              ? "선택 완료"
              : selectingPlayer === "WHITE"
                ? "선택 중..."
                : "대기 중..."}
          </span>
          {" | "}
          <span style={{ color: blackSelected ? "#4caf50" : "#ff9800" }}>
            흑:{" "}
            {blackSelected
              ? "선택 완료"
              : selectingPlayer === "BLACK"
                ? "선택 중..."
                : "대기 중..."}
          </span>
        </div>

        <div style={noteStyles}>
          같은 플레이어가 다시 증강을 고를 때 기존 증강 위에 추가로 누적됩니다.
        </div>

        <div style={progressGridStyles}>
          <div style={progressCardStyles}>
            <div style={progressHeaderStyles}>
              <span style={progressTitleStyles}>백 누적 증강</span>
              <span style={progressCountStyles}>
                {whiteActiveAugmentations.length}개
              </span>
            </div>
            {whiteActiveAugmentations.length > 0 ? (
              <ul style={progressListStyles}>
                {whiteActiveAugmentations.map((aug) => (
                  <li key={aug.id}>{aug.nameKo ?? aug.name}</li>
                ))}
              </ul>
            ) : (
              <p style={progressEmptyStyles}>아직 누적된 증강이 없습니다</p>
            )}
            {selectingPlayer === "WHITE" && stagedSelection && (
              <p style={progressPreviewStyles}>
                이번 선택 예정: {stagedSelection.nameKo ?? stagedSelection.name}
              </p>
            )}
            {whiteSelected && selectingPlayer !== "WHITE" && (
              <p style={progressPreviewStyles}>
                이번 라운드 선택 완료 (상대 공개 대기)
              </p>
            )}
          </div>

          <div style={progressCardStyles}>
            <div style={progressHeaderStyles}>
              <span style={progressTitleStyles}>흑 누적 증강</span>
              <span style={progressCountStyles}>
                {blackActiveAugmentations.length}개
              </span>
            </div>
            {blackActiveAugmentations.length > 0 ? (
              <ul style={progressListStyles}>
                {blackActiveAugmentations.map((aug) => (
                  <li key={aug.id}>{aug.nameKo ?? aug.name}</li>
                ))}
              </ul>
            ) : (
              <p style={progressEmptyStyles}>아직 누적된 증강이 없습니다</p>
            )}
            {selectingPlayer === "BLACK" && stagedSelection && (
              <p style={progressPreviewStyles}>
                이번 선택 예정: {stagedSelection.nameKo ?? stagedSelection.name}
              </p>
            )}
            {blackSelected && selectingPlayer !== "BLACK" && (
              <p style={progressPreviewStyles}>
                이번 라운드 선택 완료 (상대 공개 대기)
              </p>
            )}
          </div>
        </div>

        {/* Current player indicator */}
        {selectingPlayer && (
          <div
            style={{
              textAlign: "center",
              margin: "16px 0",
              fontSize: "1.1rem",
              fontWeight: "bold",
              color: "#333",
              backgroundColor:
                selectingPlayer === "WHITE" ? "#e3f2fd" : "#f5f5f5",
              padding: "8px",
              borderRadius: "4px",
              border: "1px solid #ddd",
            }}
          >
            {getColorDisplayName(selectingPlayer)} 선택 차례
          </div>
        )}

        {/* Augmentation choices */}
        <div style={choicesContainerStyles}>
          {augmentationChoices.map((aug) => (
            <AugmentationCard
              key={aug.id}
              augmentation={aug}
              onSelect={() => setStagedSelectionId(aug.id)}
              isSelected={stagedSelectionId === aug.id}
              disabled={!selectingPlayer}
            />
          ))}
        </div>

        <div style={actionsStyles}>
          <p style={selectedPreviewStyles}>
            {stagedSelection
              ? `선택 예정: ${stagedSelection.nameKo ?? stagedSelection.name}`
              : "먼저 카드 하나를 선택하세요"}
          </p>
          <button
            type="button"
            style={{
              ...approveButtonStyles,
              opacity: canConfirmSelection ? 1 : 0.5,
              cursor: canConfirmSelection ? "pointer" : "not-allowed",
            }}
            disabled={!canConfirmSelection}
            onClick={handleConfirmSelection}
          >
            선택 승인
          </button>
        </div>
      </div>
    </div>
  );
}
